import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import DriftWall from './DriftWall';
import './Archive.css';

const labels = {
  de: { empty: 'Noch keine Aufnahmen oder Fotos vorhanden.', all: 'Alle', video: 'Aufnahmen', photo: 'Fotos', recording: 'Aufnahme', snapshot: 'Foto', pcap: 'PCAP laden', open: 'Original öffnen', view: 'Ansehen', close: 'Schließen', remove: 'Löschen', deleting: 'Wird gelöscht …', confirmDelete: 'Dieses Medium einschließlich zugehörigem Vorschaubild und PCAP dauerhaft löschen?', deleteFailed: 'Medium konnte nicht gelöscht werden.', previewView: 'Vorschau', tableView: 'Tabelle', creator: 'User', date: 'Datum', type: 'Typ', filename: 'Datei', size: 'Größe', actions: 'Aktionen', unknownCreator: '–' },
  en: { empty: 'No recordings or photos are available yet.', all: 'All', video: 'Recordings', photo: 'Photos', recording: 'Recording', snapshot: 'Photo', pcap: 'Download PCAP', open: 'Open original', view: 'View', close: 'Close', remove: 'Delete', deleting: 'Deleting …', confirmDelete: 'Permanently delete this media item, including its thumbnail and associated PCAP?', deleteFailed: 'The media item could not be deleted.', previewView: 'Preview', tableView: 'Table', creator: 'User', date: 'Date', type: 'Type', filename: 'File', size: 'Size', actions: 'Actions', unknownCreator: '–' }
};

const ArchiveWall = () => {
  const [items, setItems] = useState(window.candyboxArchiveItems || []);
  const [language, setLanguage] = useState(window.candyboxLanguage || 'de');
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState(() => {
    try {
      return window.localStorage.getItem('candyboxArchiveView') === 'table' ? 'table' : 'preview';
    } catch {
      return 'preview';
    }
  });
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState('');
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    const updateItems = value => setItems(Array.isArray(value) ? value : []);
    const onRecordings = event => updateItems(event.detail);
    const onLanguage = event => setLanguage(event.detail || 'de');
    window.candyboxArchiveUpdate = updateItems;
    window.addEventListener('candybox:recordings', onRecordings);
    window.addEventListener('candybox:language', onLanguage);
    updateItems(window.candyboxArchiveItems);
    return () => {
      window.removeEventListener('candybox:recordings', onRecordings);
      window.removeEventListener('candybox:language', onLanguage);
      if (window.candyboxArchiveUpdate === updateItems) {
        delete window.candyboxArchiveUpdate;
      }
    };
  }, []);

  useEffect(() => {
    if (!selected) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape' && !deleting) setSelected(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [selected, deleting]);

  const visibleItems = filter === 'all' ? items : items.filter(item => item.kind === filter);
  const videoCount = items.filter(item => item.kind === 'video').length;
  const photoCount = items.filter(item => item.kind === 'photo').length;
  const locale = language === 'de' ? 'de-DE' : 'en-GB';
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'medium'
  });
  const displayItems = visibleItems.map(item => {
    const timestamp = Number(item.modified) * 1000;
    const dateLabel = Number.isFinite(timestamp) && timestamp > 0
      ? dateFormatter.format(new Date(timestamp))
      : '–';
    const typeLabel = item.kind === 'photo' ? labels[language].snapshot : labels[language].recording;
    return {
      ...item,
      name: item.name || item.title,
      dateLabel,
      detailLabel: `${typeLabel}${item.sizeMb != null ? ` · ${item.sizeMb} MB` : ''}${item.pcapHref ? ' · PCAP' : ''}`,
      pcapLabel: labels[language].pcap,
      deleteLabel: labels[language].remove,
      creatorInitials: item.creator?.initials || labels[language].unknownCreator,
      creatorName: item.creator?.display_name || labels[language].unknownCreator
    };
  }).sort((left, right) => Number(right.modified || 0) - Number(left.modified || 0));

  const deleteItem = async item => {
    if (!item || deleting || !window.confirm(labels[language].confirmDelete)) return;
    setDeleting(item.href);
    setDeleteError('');
    try {
      if (typeof window.candyboxDeleteMedia !== 'function') throw new Error('Delete handler unavailable');
      await window.candyboxDeleteMedia(item);
      if (selected?.href === item.href) setSelected(null);
    } catch (error) {
      setDeleteError(error?.message || labels[language].deleteFailed);
    } finally {
      setDeleting('');
    }
  };

  const changeView = mode => {
    setViewMode(mode);
    try {
      window.localStorage.setItem('candyboxArchiveView', mode);
    } catch {
      // The view still changes when browser storage is disabled.
    }
  };

  const viewControls = document.getElementById('archive-view-controls');

  return (
    <div className="archive-browser">
      {viewControls ? createPortal(
        <div className="archive-view-toggle" role="group" aria-label="Archive view">
          <button type="button" className={viewMode === 'preview' ? 'active' : ''} onClick={() => changeView('preview')}>{labels[language].previewView}</button>
          <button type="button" className={viewMode === 'table' ? 'active' : ''} onClick={() => changeView('table')}>{labels[language].tableView}</button>
        </div>,
        viewControls
      ) : null}
      <nav className="archive-filters" aria-label="Media filter">
        {[
          ['all', labels[language].all, items.length],
          ['video', labels[language].video, videoCount],
          ['photo', labels[language].photo, photoCount]
        ].map(([value, label, count]) => (
          <button
            type="button"
            key={value}
            className={filter === value ? 'active' : ''}
            onClick={() => setFilter(value)}
            disabled={!count}
          >
            {label}<span>{count}</span>
          </button>
        ))}
      </nav>
      {deleteError && !selected ? <p className="archive-delete-error" role="alert">{deleteError}</p> : null}
      {visibleItems.length && viewMode === 'preview' ? (
        <div className="archive-wall">
          <DriftWall
            items={displayItems}
            columns={5}
            tileWidth={200}
            tileHeight={132}
            gap={18}
            tilt={16}
            turn={-14}
            perspective={1200}
            depth={120}
            speed={42}
            direction="up"
            variance={0.45}
            parallax={0.6}
            lift={64}
            fade={0.6}
            dim={0.55}
            overlayColor="#0f1419"
            onItemOpen={item => {
              setDeleteError('');
              setSelected(item);
            }}
            onItemDelete={deleteItem}
          />
        </div>
      ) : null}
      {visibleItems.length && viewMode === 'table' ? (
        <div className="archive-table-wrap">
          <table className="archive-table">
            <thead><tr><th className="archive-user-column">{labels[language].creator}</th><th>{labels[language].date}</th><th>{labels[language].type}</th><th>{labels[language].filename}</th><th>{labels[language].size}</th><th>{labels[language].actions}</th></tr></thead>
            <tbody>{displayItems.map(item => (
              <tr key={item.href}>
                <td className="archive-user-column"><span className="archive-user" title={item.creatorName}>{item.creatorInitials}</span></td>
                <td>{item.dateLabel}</td>
                <td>{item.kind === 'photo' ? labels[language].snapshot : labels[language].recording}</td>
                <td className="archive-file" title={item.name}>{item.name}</td>
                <td>{item.sizeMb != null ? `${item.sizeMb} MB` : '–'}</td>
                <td><div className="archive-row-actions">
                  <button type="button" onClick={() => setSelected(item)}>{labels[language].view}</button>
                  <a href={item.href} target="_blank" rel="noreferrer noopener">{labels[language].open}</a>
                  {item.pcapHref ? <a href={item.pcapHref} download>{labels[language].pcap}</a> : null}
                  <button type="button" className="danger" onClick={() => deleteItem(item)} disabled={deleting === item.href}>{deleting === item.href ? labels[language].deleting : labels[language].remove}</button>
                </div></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : null}
      {!visibleItems.length ? <div className="archive-empty">{labels[language].empty}</div> : null}
      {selected ? (
        <div
          className="archive-viewer"
          role="dialog"
          aria-modal="true"
          aria-label={selected.name}
          onMouseDown={event => {
            if (event.target === event.currentTarget && !deleting) setSelected(null);
          }}
        >
          <section className="archive-viewer__shell">
            <header>
              <div>
                <strong>{selected.name}</strong>
                <span>{selected.dateLabel} · {selected.detailLabel}</span>
              </div>
              <button type="button" onClick={() => setSelected(null)} disabled={Boolean(deleting)} aria-label={labels[language].close}>×</button>
            </header>
            <div className="archive-viewer__media">
              {selected.kind === 'video' ? (
                <video src={selected.href} controls playsInline preload="metadata" />
              ) : (
                <img src={selected.href} alt={selected.name} />
              )}
            </div>
            {deleteError ? <p className="archive-viewer__error" role="alert">{deleteError}</p> : null}
            <footer>
              <div>
                <a href={selected.href} target="_blank" rel="noreferrer noopener">{labels[language].open}</a>
                {selected.pcapHref ? <a href={selected.pcapHref} download>{labels[language].pcap}</a> : null}
              </div>
              <button type="button" className="archive-delete" onClick={() => deleteItem(selected)} disabled={Boolean(deleting) || !selected.deleteHref}>
                {deleting === selected.href ? labels[language].deleting : labels[language].remove}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </div>
  );
};

const root = document.getElementById('archive-root');
if (root) {
  window.candyboxArchiveMounted = true;
  createRoot(root).render(<ArchiveWall />);
}
